/**
 * Price touch markets (#786): HIP-4's native standalone Yes/No templates `priceTouch` (mainnet + testnet) and
 * `priceTouch2` (testnet), "{perp} touches {target} by {time}". Research: docs/research/TOUCH_CONTRACTS.md.
 *
 * Rules (shared with Android/iOS through shared/fixtures/describe.json):
 *  - A touch market is any outcome whose template FAMILY (version digits stripped) is `priceTouch`.
 *  - It resolves Yes as soon as the {seconds}-second TWAP of {priceDescription} equals or crosses {target} at any time
 *    after listing and at or before {time}; otherwise No at {time}. So {time} is a DEADLINE, not a settlement time:
 *    the market can settle early.
 *  - Direction is implicit (not in the meta): the touch is "up" if the target was above the price at listing. A client
 *    can only say where the target is relative to the CURRENT mark (touchDistance), never whether it was touched.
 * Pure: no React, no network.
 */
import { cleanDecimal, formatNumber, formatWithUnit, parseHlTime, priceUnit, stripDex } from './format';
import { parseDescription } from './parse';
import { familyOf, templateId } from './templates';

export const TOUCH_FAMILY = 'priceTouch';

/** Everything the UI needs about a touch market, parsed from its outcomeMeta name + description. */
export interface TouchInfo {
  /** Full perp name as on-chain ("BTC", "xyz:CL"). */
  perp: string;
  /** Perp without its builder-dex prefix ("CL"). */
  underlying: string;
  /** Builder dex ("xyz"), "" for the main perp dex. */
  dex: string;
  /** Target exactly as on-chain (trimmed). */
  target: string;
  /** Target at display precision with its unit ("$87,500"). */
  targetLabel: string;
  /** Touch-by deadline ({time}). */
  deadline: Date;
  /** TWAP window in seconds ({seconds}); absent when missing or not a positive integer. */
  twapSeconds?: number;
  /** Price measured ({priceDescription} without a leading "the "), e.g. "BTC-USDC perp mark". */
  priceSource?: string;
  /** One-sentence resolution rule for the UI. */
  rule: string;
}

const DECIMAL = /^\d+(\.\d+)?$|^\.\d+$/;

/** True for template names of the priceTouch family ("template:priceTouch", "template:priceTouch2", …). */
export function isTouchTemplate(name: string | null | undefined): boolean {
  const id = templateId(name);
  return id !== null && familyOf(id) === TOUCH_FAMILY;
}

/** "BTC-USDC perp mark" → "the 3-second TWAP of the BTC-USDC perp mark price". */
function measured(src: string | undefined, underlying: string, twapSeconds: number | undefined): string {
  const base = src ? `the ${src}` : `the ${underlying} mark`;
  const price = /\bprice$/i.test(base) ? base : `${base} price`;
  return twapSeconds ? `the ${formatNumber(String(twapSeconds))}-second TWAP of ${price}` : price;
}

/**
 * Touch info for an outcome, or null when it is not a priceTouch-family template or a required keyword is missing
 * or malformed: `perp` (non-empty), `target` (plain positive decimal) and `time` (valid "YYYYMMDD-HHMM").
 */
export function touchInfo(name: string | null | undefined, description: string | null | undefined): TouchInfo | null {
  if (!isTouchTemplate(name)) return null;
  const d = parseDescription(description);
  const perp = (d.perp ?? '').trim();
  const target = (d.target ?? '').trim();
  const deadline = parseHlTime(d.time);
  if (!perp || !DECIMAL.test(target) || !/[1-9]/.test(target) || !deadline) return null;
  const i = perp.lastIndexOf(':');
  const underlying = stripDex(perp);
  const secs = /^\d+$/.test(d.seconds ?? '') ? Number(d.seconds) : 0;
  const twapSeconds = secs > 0 ? secs : undefined;
  const priceSource = d.priceDescription?.replace(/^the\s+/i, '').trim() || undefined;
  const targetLabel = formatWithUnit(target, priceUnit(perp, d.priceDescription));
  const rule =
    `Resolves Yes as soon as ${measured(priceSource, underlying, twapSeconds)} equals or crosses ${targetLabel} ` +
    `at or before the deadline, so the market can settle early. Otherwise it resolves No at the deadline.`;
  return {
    perp,
    underlying,
    dex: i >= 0 ? perp.slice(0, i) : '',
    target,
    targetLabel,
    deadline,
    ...(twapSeconds ? { twapSeconds } : {}),
    ...(priceSource ? { priceSource } : {}),
    rule,
  };
}

export type TouchDirection = 'up' | 'down' | 'at';

export interface TouchDistance {
  /** Where the target is relative to the current mark: above ("up"), below ("down") or equal ("at"). */
  direction: TouchDirection;
  /**
   * Signed move needed, in percent of the mark, rounded half-up on the absolute value to 1 decimal:
   * sign(t - m) * floor(|t - m| / m * 100 * 10 + 0.5) / 10 (IEEE doubles). 0 for "at".
   */
  pct: number;
  /** "Needs +7.8% (up)", "Needs −4.5% (down)", "Needs <0.1% (up)", "At the target". */
  label: string;
}

const num = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v;
  if (typeof v !== 'string' || !DECIMAL.test(v.trim())) return Number.NaN;
  return Number(v.trim());
};

/**
 * Distance from the current mark to the target, or null when either is not a positive finite number. Strings must be
 * plain decimals ("81170.5"); numbers are used as-is. A hint only: it says nothing about whether the market touched.
 */
export function touchDistance(target: string | number | null | undefined, mark: string | number | null | undefined): TouchDistance | null {
  const t = num(target);
  const m = num(mark);
  if (!Number.isFinite(t) || !Number.isFinite(m) || t <= 0 || m <= 0) return null;
  if (t === m) return { direction: 'at', pct: 0, label: 'At the target' };
  const up = t > m;
  const abs = Math.floor((Math.abs(t - m) / m) * 100 * 10 + 0.5) / 10;
  const dir: TouchDirection = up ? 'up' : 'down';
  const shown = abs === 0 ? '<0.1%' : `${up ? '+' : '−'}${abs.toFixed(1)}%`;
  return { direction: dir, pct: abs === 0 ? 0 : up ? abs : -abs, label: `Needs ${shown} (${dir})` };
}

export interface TouchSettlement {
  /** Settled strictly before the deadline. */
  early: boolean;
  label: string;
}

/**
 * How a touch market settled. `deadline` is the description {time} ("YYYYMMDD-HHMM"), `settledAt` an ISO time,
 * `settleFraction` the on-chain fraction ("1" Yes, "0" No). Null when the deadline or settledAt does not parse.
 *  - "1" before the deadline → early, "Settled early: target touched"; "1" at/after → "Settled Yes: target touched".
 *  - "0" before the deadline → early, "Settled No before the deadline" (delisting); "0" at/after →
 *    "Settled No: not touched by the deadline".
 *  - anything else → "Settled at <fraction>" (early when before the deadline).
 */
export function touchSettlement(deadline: string | null | undefined, settledAt: string | null | undefined, settleFraction: string | null | undefined): TouchSettlement | null {
  const dl = parseHlTime(deadline);
  const at = typeof settledAt === 'string' ? new Date(settledAt) : undefined;
  if (!dl || !at || Number.isNaN(at.getTime())) return null;
  const early = at.getTime() < dl.getTime();
  const f = (settleFraction ?? '').trim();
  if (f === '1') return { early, label: early ? 'Settled early: target touched' : 'Settled Yes: target touched' };
  if (f === '0') return { early, label: early ? 'Settled No before the deadline' : 'Settled No: not touched by the deadline' };
  return { early, label: `Settled at ${cleanDecimal(f) || '?'}` };
}
